import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api-client';
import type { TeamMember } from '../queries/team';

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['team', 'invite'],
    mutationFn: async ({
      email,
      role,
    }: {
      email: string;
      role: TeamMember['role'];
    }) => {
      const res = await apiFetch('/api/console/team/invite', {
        method: 'POST',
        body: JSON.stringify({ email, role }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          (err as { error?: string }).error ?? 'Failed to invite member',
        );
      }
      return (await res.json()).member as TeamMember;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });
}

export function useUpdateMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['team', 'update-role'],
    mutationFn: async ({
      id,
      role,
    }: {
      id: string;
      role: TeamMember['role'];
    }) => {
      const res = await apiFetch(`/api/console/team/${id}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      });
      if (!res.ok) throw new Error('Failed to update role');
      return (await res.json()).member as TeamMember;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['team', 'remove'],
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/api/console/team/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to remove member');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });
}
